import { useCallback, useEffect, useState } from 'react'
import { deployedBuild, isNewer, type DeployedBuild } from '../lib/update'

/**
 * How often to look while the app simply sits open.
 *
 * Deliberately lazy. The check that MATTERS is the one on returning to the app,
 * because that is when a deploy which happened while it was closed becomes
 * relevant. The timer only exists for the phone left on a counter all evening,
 * and it costs one small static request.
 */
const CHECK_EVERY_MS = 30 * 60_000

export interface UpdateApi {
  available: DeployedBuild | null
  reload: () => void
  dismiss: () => void
}

export function useAppUpdate(): UpdateApi {
  const [available, setAvailable] = useState<DeployedBuild | null>(null)
  const [dismissed, setDismissed] = useState(false)

  const check = useCallback(async () => {
    const deployed = await deployedBuild()
    if (isNewer(deployed)) setAvailable(deployed)
  }, [])

  useEffect(() => {
    void check()

    /*
     * Coming back to the app is the moment worth checking. A deploy that landed
     * while the phone was in a pocket is exactly the update somebody is running
     * without knowing, and `visibilitychange` is the only event a home-screen
     * app or a TWA reliably fires when it is opened again.
     */
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
    }
  }, [check])

  /**
   * Take the update.
   *
   * A plain reload is enough, and the reason is worth stating: the service
   * worker serves navigations network-first, so a reload fetches the new
   * index.html, which names new content-hashed assets, which are cache misses,
   * which are fetched.
   */
  const reload = useCallback(() => { window.location.reload() }, [])

  return { available: dismissed ? null : available, reload, dismiss: () => setDismissed(true) }
}
